import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';

// Parse prize text: "Gift Card 10 EUR" -> { amount: "€10", label: "Gift Card", hasGiftCard: true }
function parsePrizeText(text) {
  if (!text || typeof text !== 'string') return { amount: null, label: null, hasGiftCard: false };
  const lower = text.toLowerCase();
  const hasGiftCard = lower.includes('gift') || lower.includes('card');

  const eurMatch = text.match(/(\d+)\s*(?:EUR|€|euros?)/i) || text.match(/€\s*(\d+)/i);
  const amount = eurMatch ? `€${eurMatch[1]}` : null;

  let label = null;
  if (amount && hasGiftCard) {
    label = text.replace(/\d+\s*(?:EUR|€|euros?)/gi, '').replace(/€\s*\d+/gi, '').trim();
    if (!label || label.length < 2) label = 'Gift Card';
  } else if (amount) {
    label = text.replace(/\d+\s*(?:EUR|€|euros?)/gi, '').replace(/€\s*\d+/gi, '').trim();
    if (!label || label.length < 2) label = 'Gift Card';
  }

  return { amount, label: label || (amount ? 'Gift Card' : null), hasGiftCard };
}

function truncateToFit(ctx, text, maxWidth, fontSize) {
  if (!text) return '';
  ctx.font = `${fontSize}px "Oswald", Arial`;
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 1 && ctx.measureText(truncated + '…').width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + (truncated.length < text.length ? '…' : '');
}

function drawReferenceText(ctx, text, x, y, fontSize, fontWeight, isAmount) {
  const font = `${fontWeight} ${fontSize}px "Oswald", "Segoe UI", Arial, sans-serif`;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#d4af37';
  if (isAmount) {
    ctx.shadowColor = 'rgba(212, 175, 55, 0.5)';
    ctx.shadowBlur = 12;
  } else {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 3;
  }
  ctx.fillText(text, x, y);
  ctx.shadowBlur = 0;
}

function colorToRgb(color) {
  const hexMatch = color.match(/^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  }
  const hslMatch = color.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/);
  if (hslMatch) {
    const h = parseFloat(hslMatch[1]) / 360;
    const s = parseFloat(hslMatch[2]) / 100;
    const l = parseFloat(hslMatch[3]) / 100;
    let r, g, b;
    if (s === 0) r = g = b = l;
    else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  }
  return { r: 128, g: 128, b: 128 };
}

// Reference palette: Green, Red, Blue, Purple - with light/dark variants
const REFERENCE_COLORS = [
  { base: '#0ea043', light: '#0d7a35', dark: '#0a6b2c' },
  { base: '#b82828', light: '#8c1b1b', dark: '#7a1616' },
  { base: '#2558ad', light: '#173d82', dark: '#143572' },
  { base: '#6a30a5', light: '#4a1b72', dark: '#3f1560' },
  { base: '#e67e22', light: '#d35400', dark: '#a04000' },
  { base: '#16a085', light: '#0e6655', dark: '#0d4d3d' },
];

function getSegmentColors(count) {
  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(REFERENCE_COLORS[i % REFERENCE_COLORS.length]);
  }
  return result;
}

const SpinningWheel = forwardRef(({ prizes, onSpinComplete, onSpinStart, disabled, onRecordingComplete, actualWinner, drawType = 'vote' }, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const currentRotationRef = useRef(0);
  const wheelSegmentsRef = useRef([]);
  const animationIdRef = useRef(null);
  const segmentColorsRef = useRef([]);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingCanvasRef = useRef(null);
  const recordingCtxRef = useRef(null);
  const winnerDisplayIntervalRef = useRef(null);
  const isRecordingRef = useRef(false);
  const recordingAnimationRef = useRef(null);

  useEffect(() => {
    const recordingCanvas = document.createElement('canvas');
    recordingCanvas.width = 800;
    recordingCanvas.height = 900;
    recordingCanvasRef.current = recordingCanvas;
    recordingCtxRef.current = recordingCanvas.getContext('2d');
  }, []);

  const drawWheel = useCallback((rotation = 0) => {
    if (!canvasRef.current || !prizes || prizes.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 4;
    const dividerBase = '#000';

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    ctx.translate(-centerX, -centerY);

    wheelSegmentsRef.current.forEach((segment) => {
      const midAngle = (segment.startAngle + segment.endAngle) / 2;
      const x1 = centerX + Math.cos(segment.startAngle) * radius * 0.3;
      const y1 = centerY + Math.sin(segment.startAngle) * radius * 0.3;
      const x2 = centerX + Math.cos(midAngle) * radius;
      const y2 = centerY + Math.sin(midAngle) * radius;

      const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
      gradient.addColorStop(0, segment.colorLight || segment.color);
      gradient.addColorStop(0.5, segment.color);
      gradient.addColorStop(1, segment.colorDark || segment.color);

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, segment.startAngle, segment.endAngle);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    });

    const innerShadow = ctx.createRadialGradient(centerX, centerY, radius * 0.65, centerX, centerY, radius);
    innerShadow.addColorStop(0, 'transparent');
    innerShadow.addColorStop(1, 'rgba(0,0,0,0.25)');
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = innerShadow;
    ctx.fill();

    const segments = wheelSegmentsRef.current;
    segments.forEach((segment) => {
      const rgb = colorToRgb(segment.color);
      const glowRgba = `rgba(${rgb.r},${rgb.g},${rgb.b},0.85)`;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX + Math.cos(segment.startAngle) * radius, centerY + Math.sin(segment.startAngle) * radius);
      ctx.strokeStyle = dividerBase;
      ctx.lineWidth = 5;
      ctx.shadowColor = glowRgba;
      ctx.shadowBlur = 12;
      ctx.stroke();
    });
    ctx.shadowBlur = 0;

    segments.forEach((segment) => {
      const brightColor = segment.colorLight || segment.color;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX + Math.cos(segment.startAngle) * radius, centerY + Math.sin(segment.startAngle) * radius);
      ctx.strokeStyle = brightColor;
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    wheelSegmentsRef.current.forEach((segment) => {
      const midAngle = (segment.startAngle + segment.endAngle) / 2;
      const textRadius = radius * 0.65;
      const textX = centerX + Math.cos(midAngle) * textRadius;
      const textY = centerY + Math.sin(midAngle) * textRadius;

      ctx.save();
      ctx.translate(textX, textY);
      ctx.rotate(midAngle + Math.PI / 2);

      const name = String(segment.prize.price || '').trim();
      const { amount, label, hasGiftCard } = parsePrizeText(name);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const primaryFontSize = 40;
      const secondaryFontSize = 13;

      let totalHeight = 0;
      if (hasGiftCard) totalHeight += 20 + 4;
      if (amount) totalHeight += primaryFontSize + 2;
      const displayLabel = label || (amount ? '' : name);
      if (displayLabel) totalHeight += secondaryFontSize + 2;
      else if (!amount && name) totalHeight += primaryFontSize;
      const startY = -totalHeight / 2;
      let drawY = startY;

      if (hasGiftCard) {
        const iconW = 28;
        const iconH = 20;
        const iconX = -iconW / 2;
        const iconY = drawY;
        ctx.strokeStyle = 'rgba(210, 175, 70, 0.7)';
        ctx.lineWidth = 2;
        ctx.strokeRect(iconX, iconY, iconW, iconH);
        ctx.fillStyle = 'rgba(210, 175, 70, 0.6)';
        ctx.fillRect(iconX + 4, iconY + 3, 8, 3);
        drawY += iconH + 4;
      }

      if (amount) {
        drawReferenceText(ctx, amount, 0, drawY + primaryFontSize / 2, primaryFontSize, '700', true);
        drawY += primaryFontSize + 2;
      }

      if (displayLabel) {
        ctx.font = `600 ${secondaryFontSize}px "Oswald", Arial`;
        const maxWidth = radius * 0.9;
        const truncated = truncateToFit(ctx, displayLabel, maxWidth, secondaryFontSize);
        drawReferenceText(ctx, truncated.toUpperCase(), 0, drawY + secondaryFontSize / 2, secondaryFontSize, '600', false);
      } else if (!amount && name) {
        ctx.font = `600 ${secondaryFontSize}px "Oswald", Arial`;
        const truncated = truncateToFit(ctx, name, radius * 0.9, secondaryFontSize);
        drawReferenceText(ctx, truncated.toUpperCase(), 0, drawY + secondaryFontSize / 2, secondaryFontSize, '600', false);
      }
      ctx.restore();
    });

    ctx.restore();
  }, [prizes]);

  useEffect(() => {
    if (!prizes || prizes.length === 0) return;

    const segmentAngle = (2 * Math.PI) / prizes.length;
    const colorSet = getSegmentColors(prizes.length);

    wheelSegmentsRef.current = [];
    segmentColorsRef.current = colorSet;
    let currentAngle = -Math.PI / 2;

    prizes.forEach((prize, index) => {
      const c = colorSet[index];
      wheelSegmentsRef.current.push({
        prize,
        startAngle: currentAngle,
        endAngle: currentAngle + segmentAngle,
        color: c.base,
        colorLight: c.light,
        colorDark: c.dark
      });
      currentAngle += segmentAngle;
    });

    currentRotationRef.current = 0;
    drawWheel(0);
  }, [prizes, drawWheel]);

  const drawRecordingFrame = useCallback((winnerInfo = null, spinningText = false, showLoading = false) => {
    const ctx = recordingCtxRef.current;
    const canvas = recordingCanvasRef.current;
    if (!ctx || !canvas || !canvasRef.current) return;

    const sceneSize = 560;
    const sceneX = (canvas.width - sceneSize) / 2;
    const sceneY = 80;
    const cx = sceneX + sceneSize / 2;
    const cy = sceneY + sceneSize / 2;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const drawTypeLabel = drawType === 'upload' ? 'UPLOAD' : 'VOTES';
    ctx.fillStyle = '#d4af37';
    ctx.shadowColor = 'rgba(212, 175, 55, 0.5)';
    ctx.shadowBlur = 12;
    ctx.font = 'bold 32px "Oswald", Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Prize Draw (${drawTypeLabel})`, canvas.width / 2, 45);
    ctx.shadowBlur = 0;

    const LED_COLORS = ['#39ff14', '#ffe600', '#ff3344', '#00e5ff', '#7b2fff', '#ff00c8'];
    for (let i = 0; i < 72; i++) {
      const angle = (i / 72) * 2 * Math.PI - Math.PI / 2;
      const x = cx + 268 * Math.cos(angle);
      const y = cy + 268 * Math.sin(angle);
      const color = LED_COLORS[i % LED_COLORS.length];
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(cx, cy, 270, 0, 2 * Math.PI);
    ctx.arc(cx, cy, 250, 0, 2 * Math.PI, true);
    const borderGrad = ctx.createRadialGradient(cx - 50, cy - 50, 0, cx, cy, 270);
    borderGrad.addColorStop(0, '#2a2a30');
    borderGrad.addColorStop(0.6, '#111115');
    borderGrad.addColorStop(1, '#0a0a0e');
    ctx.fillStyle = borderGrad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, 250, 0, 2 * Math.PI);
    ctx.clip();
    ctx.drawImage(canvasRef.current, cx - 250, cy - 250, 500, 500);
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, 60, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 70, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const hubGrad = ctx.createRadialGradient(cx - 18, cy - 18, 0, cx, cy, 45);
    hubGrad.addColorStop(0, spinningText ? '#555' : '#666');
    hubGrad.addColorStop(0.15, spinningText ? '#444' : '#444');
    hubGrad.addColorStop(0.5, '#1a1a1a');
    hubGrad.addColorStop(1, '#000');
    ctx.beginPath();
    ctx.arc(cx, cy, 45, 0, 2 * Math.PI);
    ctx.fillStyle = hubGrad;
    ctx.fill();
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 3;
    ctx.stroke();
    [['#1a1a1a', 47], ['#444', 49], ['#222', 51], ['#111', 53]].forEach(([color, r]) => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    if (spinningText) {
      const spinAngle = (Date.now() / 70) % 360 * (Math.PI / 180);
      const r = 22;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(spinAngle);
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.shadowColor = 'rgba(0,229,255,0.6)';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(0, 0, r, -Math.PI * 0.5, 0);
      ctx.strokeStyle = '#00e5ff';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.shadowColor = 'rgba(123,47,255,0.5)';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 0.5);
      ctx.strokeStyle = '#7b2fff';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.restore();
    } else {
      ctx.fillStyle = '#ccc';
      ctx.font = '900 15px "Inter", Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(200,200,200,0.4)';
      ctx.shadowBlur = 10;
      ctx.fillText('SPIN!', cx, cy);
      ctx.shadowBlur = 0;
    }

    const ptrTop = sceneY - 6;
    const ptrCx = cx;
    const mountW = 24;
    const mountH = 14;
    const ballR = 16;
    const arrowH = 22;
    const ballY = ptrTop + mountH + ballR;
    const arrowTop = ballY + ballR;

    const mountGrad = ctx.createLinearGradient(ptrCx, ptrTop, ptrCx, ptrTop + mountH);
    mountGrad.addColorStop(0, '#999');
    mountGrad.addColorStop(1, '#555');
    ctx.fillStyle = mountGrad;
    ctx.fillRect(ptrCx - mountW / 2, ptrTop, mountW, mountH);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(ptrCx - mountW / 2, ptrTop, mountW, mountH);

    ctx.shadowColor = 'rgba(0,255,150,0.9)';
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(ptrCx, ballY, ballR, 0, 2 * Math.PI);
    const ballGrad = ctx.createRadialGradient(ptrCx - 5, ballY - 5, 0, ptrCx, ballY, ballR);
    ballGrad.addColorStop(0, '#80ffcc');
    ballGrad.addColorStop(0.5, '#00dd88');
    ballGrad.addColorStop(1, '#006644');
    ctx.fillStyle = ballGrad;
    ctx.fill();
    ctx.strokeStyle = '#bbb';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(ptrCx + 4, ballY - 2, 4, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.shadowColor = 'rgba(0,255,150,0.9)';
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.moveTo(ptrCx, arrowTop + arrowH);
    ctx.lineTo(ptrCx - 12, arrowTop);
    ctx.lineTo(ptrCx + 12, arrowTop);
    ctx.closePath();
    ctx.fillStyle = '#00cc88';
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 2;
    ctx.stroke();

    if (showLoading && !winnerInfo) {
      const loaderY = sceneY + sceneSize + 30;
      ctx.fillStyle = '#1a1a2e';
      ctx.beginPath();
      ctx.roundRect(80, loaderY, canvas.width - 160, 80, 16);
      ctx.fill();
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 22px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Selecting winner...', canvas.width / 2, loaderY + 45);
    }

    if (winnerInfo) {
      const winnerY = sceneY + sceneSize + 30;
      ctx.fillStyle = '#1a1a2e';
      ctx.beginPath();
      ctx.roundRect(80, winnerY, canvas.width - 160, 140, 16);
      ctx.fill();
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 28px Oswald, Arial';
      ctx.textAlign = 'center';
      ctx.fillText('🎉 Winner! 🎉', canvas.width / 2, winnerY + 40);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 24px Arial';
      const winnerName = winnerInfo.name || winnerInfo.username || winnerInfo.email || 'Winner';
      ctx.fillText(winnerName, canvas.width / 2, winnerY + 80);
      ctx.font = '20px Arial';
      ctx.fillText(`Prize: ${winnerInfo.prize}`, canvas.width / 2, winnerY + 115);
    }

    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '12px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(new Date().toLocaleString(), canvas.width - 20, canvas.height - 15);
  }, [drawType]);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    if (recordingAnimationRef.current) {
      cancelAnimationFrame(recordingAnimationRef.current);
      recordingAnimationRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const startRecording = useCallback(() => {
    const recordingCanvas = recordingCanvasRef.current;
    if (!recordingCanvas) return false;
    try {
      recordedChunksRef.current = [];
      setRecordedVideoUrl(null);
      const stream = recordingCanvas.captureStream(60);
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : MediaRecorder.isTypeSupported('video/webm;codecs=vp8') ? 'video/webm;codecs=vp8' : 'video/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 10000000 });
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordedVideoUrl(url);
        setIsRecording(false);
        isRecordingRef.current = false;
        if (onRecordingComplete) onRecordingComplete(url, blob);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(50);
      setIsRecording(true);
      isRecordingRef.current = true;
      return true;
    } catch (e) {
      console.error('Failed to start recording:', e);
      return false;
    }
  }, [onRecordingComplete]);

  const stopRecordingWithWinnerInfo = useCallback((winner) => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
    if (winnerDisplayIntervalRef.current) clearInterval(winnerDisplayIntervalRef.current);
    if (recordingAnimationRef.current) cancelAnimationFrame(recordingAnimationRef.current);
    let winnerDisplayTime = 0;
    const drawWinnerFrame = () => {
      drawRecordingFrame(winner, false);
      winnerDisplayTime += 16;
      if (winnerDisplayTime < 3000) {
        recordingAnimationRef.current = requestAnimationFrame(drawWinnerFrame);
      } else {
        stopRecording();
      }
    };
    recordingAnimationRef.current = requestAnimationFrame(drawWinnerFrame);
  }, [drawRecordingFrame, stopRecording]);

  useEffect(() => {
    if (actualWinner && isRecordingRef.current && !isSpinning) {
      stopRecordingWithWinnerInfo(actualWinner);
    }
  }, [actualWinner, isSpinning, stopRecordingWithWinnerInfo]);

  const spinWheelInternal = useCallback(() => {
    if (isSpinning || !prizes || prizes.length === 0) return;
    setIsSpinning(true);
    currentRotationRef.current = 0;
    drawWheel(0);

    const randomIndex = Math.floor(Math.random() * prizes.length);
    const winner = prizes[randomIndex];
    const segment = wheelSegmentsRef.current[randomIndex];
    const segmentCenter = (segment.startAngle + segment.endAngle) / 2;
    let landingAngle = -Math.PI / 2 - segmentCenter;
    while (landingAngle < 0) landingAngle += 2 * Math.PI;
    const fullSpins = (5 + Math.floor(Math.random() * 3)) * 2 * Math.PI;
    const targetRotation = fullSpins + landingAngle;

    const startTime = performance.now();
    const duration = 4000;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      currentRotationRef.current = targetRotation * easeOut;
      drawWheel(currentRotationRef.current);
      if (isRecordingRef.current) drawRecordingFrame(null, true); // eslint-disable-line no-unused-vars
      if (progress < 1) {
        animationIdRef.current = requestAnimationFrame(animate);
      } else {
        currentRotationRef.current = currentRotationRef.current % (2 * Math.PI);
        setIsSpinning(false);
        if (isRecordingRef.current && mediaRecorderRef.current?.state === 'recording') {
          const keepDrawing = () => {
            if (!isRecordingRef.current || mediaRecorderRef.current?.state !== 'recording') return;
            drawRecordingFrame(null, false, true);
            recordingAnimationRef.current = requestAnimationFrame(keepDrawing);
          };
          recordingAnimationRef.current = requestAnimationFrame(keepDrawing);
        }
        if (onSpinComplete) onSpinComplete(winner);
      }
    };
    if (isRecordingRef.current) drawRecordingFrame(null, true);
    animationIdRef.current = requestAnimationFrame(animate);
  }, [isSpinning, prizes, drawWheel, drawRecordingFrame, onSpinComplete]);

  const startRecordingAndSpin = useCallback(() => {
    if (isSpinning || !prizes || prizes.length === 0) return;
    if (onSpinStart) onSpinStart();
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (winnerDisplayIntervalRef.current) { clearInterval(winnerDisplayIntervalRef.current); winnerDisplayIntervalRef.current = null; }
    if (recordingAnimationRef.current) { cancelAnimationFrame(recordingAnimationRef.current); recordingAnimationRef.current = null; }
    isRecordingRef.current = false;
    setIsRecording(false);
    setRecordedVideoUrl(null);
    recordedChunksRef.current = [];
    setTimeout(() => {
      const started = startRecording();
      if (isRecordingRef.current) drawRecordingFrame(null, false);
      setTimeout(() => spinWheelInternal(), started ? 100 : 0);
    }, 50);
  }, [isSpinning, prizes, startRecording, drawRecordingFrame, spinWheelInternal, onSpinStart]);

  useImperativeHandle(ref, () => ({
    startRecordingAndSpin: () => startRecordingAndSpin(),
    getRecordedVideoUrl: () => recordedVideoUrl,
    isRecording: isRecording,
    stopRecordingWithWinner: (winner) => stopRecordingWithWinnerInfo(winner),
    stopRecording: () => stopRecording()
  }));

  const spinWheel = () => startRecordingAndSpin();

  const ledDots = useMemo(() => {
    const LED_COLORS = ['#39ff14', '#ffe600', '#ff3344', '#00e5ff', '#7b2fff', '#ff00c8'];
    const count = 72;
    const centerX = 280;
    const centerY = 280;
    const radius = 268;
    const dots = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const color = LED_COLORS[i % LED_COLORS.length];
      dots.push(
        <div
          key={i}
          className="absolute rounded-full animate-led-blink"
          style={{
            width: '5px',
            height: '5px',
            left: `${x - 2.5}px`,
            top: `${y - 2.5}px`,
            background: color,
            boxShadow: `0 0 5px 1.5px ${color}`
          }}
        />
      );
    }
    return dots;
  }, []);

  useEffect(() => {
    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      if (recordingAnimationRef.current) cancelAnimationFrame(recordingAnimationRef.current);
      if (winnerDisplayIntervalRef.current) clearInterval(winnerDisplayIntervalRef.current);
      if (recordedVideoUrl) URL.revokeObjectURL(recordedVideoUrl);
    };
  }, [recordedVideoUrl]);

  return (
    <div className="flex flex-col items-center" ref={containerRef}>
      {isRecording && (
        <div className="mb-4 flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-full animate-pulse">
          <div className="w-3 h-3 bg-white rounded-full"></div>
          <span className="font-semibold">Recording...</span>
        </div>
      )}

      {/* Reference premium wheel scene - 560x560 */}
      <div className="relative" style={{ width: '560px', height: '560px' }}>
        {/* Neon glow ring - reference: inset -4px, blur 6px, opacity 0.85, 3s spin */}
        <div className="absolute inset-0 rounded-full overflow-visible pointer-events-none z-0">
          <div className="absolute rounded-full" style={{ inset: '-4px', background: 'conic-gradient(from 0deg, #ff003c, #ff6b00, #ffe600, #39ff14, #00e5ff, #7b2fff, #ff00c8, #ff003c)', filter: 'blur(6px)', opacity: 0.85, animation: 'spinGlow 3s linear infinite' }} />
        </div>

        {/* LED dot ring - colorful dots along the glow */}
        <div className="absolute inset-0 rounded-full pointer-events-none z-[1]">
          {ledDots}
        </div>

        {/* Dark metallic border ring - reference: inset 10px, bevel, inner groove */}
        <div className="absolute rounded-full pointer-events-none z-[2]" style={{ inset: '10px', background: 'radial-gradient(circle at 40% 35%, #2a2a30, #111115 60%, #0a0a0e)', boxShadow: 'inset 0 2px 6px rgba(255,255,255,0.06), inset 0 -2px 6px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.6)' }}>
          <div className="absolute rounded-full pointer-events-none bg-transparent" style={{ inset: '2px', border: '1px solid rgba(255,255,255,0.06)' }} />
          <div className="absolute rounded-full pointer-events-none bg-transparent" style={{ inset: '18px', boxShadow: 'inset 0 0 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.4)', border: '1px solid rgba(0,0,0,0.5)' }} />
        </div>

        {/* Wheel clip area - reference: inset 30px = 500x500 wheel */}
        <div className="absolute rounded-full overflow-hidden z-[3]" style={{ inset: '30px' }}>
          <canvas ref={canvasRef} width={500} height={500} style={{ display: 'block', width: '100%', height: '100%', borderRadius: '50%' }} />
        </div>

        {/* Center hub - reference: 120px, 140px rings, SPIN button with 4-6-8-10px rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="absolute rounded-full border border-white/[0.06]" style={{ width: '120px', height: '120px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          <div className="absolute rounded-full border border-white/[0.03]" style={{ width: '140px', height: '140px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          <button
            onClick={spinWheel}
            disabled={disabled || isSpinning || isRecording || !prizes || prizes.length === 0}
            className="relative w-[90px] h-[90px] rounded-full border-[3px] border-[#444] font-black text-[#ccc] text-[15px] tracking-[3px] cursor-pointer transition-all duration-250 hover:text-white hover:border-[#555] disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none"
            style={{
              background: disabled || isSpinning || isRecording
                ? 'radial-gradient(circle at 38% 28%, #555, #333 35%, #222 55%, #111 100%)'
                : 'radial-gradient(circle at 38% 28%, #666 0%, #444 15%, #2a2a2a 35%, #1a1a1a 55%, #0d0d0d 75%, #000 100%)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.9), inset 0 2px 4px rgba(255,255,255,0.18), inset 0 -3px 8px rgba(0,0,0,0.9), 0 0 0 4px #1a1a1a, 0 0 0 6px #444, 0 0 0 8px #222, 0 0 0 10px #111',
              textShadow: '0 0 10px rgba(200,200,200,0.4), 0 2px 4px rgba(0,0,0,0.9)'
            }}
          >
            {isSpinning ? (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="w-11 h-11 rounded-full"
                  style={{
                    border: '3px solid rgba(255,255,255,0.15)',
                    borderTopColor: '#00e5ff',
                    borderRightColor: '#7b2fff',
                    boxShadow: '0 0 16px rgba(0,229,255,0.6), 0 0 8px rgba(123,47,255,0.4), inset 0 0 10px rgba(0,0,0,0.4)',
                    animation: 'spin 0.7s linear infinite'
                  }}
                />
              </div>
            ) : (
              'SPIN!'
            )}
          </button>
        </div>

        {/* Pointer - reference: mount 24x14, ball 32px, arrow */}
        <div className="absolute -top-[6px] left-1/2 -translate-x-1/2 z-20 flex flex-col items-center">
          <div className="w-6 h-3.5 rounded-t-[4px]" style={{ background: 'linear-gradient(to bottom, #999, #555)', boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)' }} />
          <div className="w-8 h-8 rounded-full border-[3px] border-[#bbb] relative" style={{ background: 'radial-gradient(circle at 35% 30%, #80ffcc, #00dd88 45%, #009966 80%, #006644)', boxShadow: '0 0 14px rgba(0,255,150,0.6), 0 0 30px rgba(0,255,150,0.25), inset 0 -3px 6px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.3)' }}>
            <div className="absolute top-1 left-1.5 w-2 h-1.5 rounded-full bg-white/50" style={{ transform: 'rotate(-20deg)' }} />
          </div>
          <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[22px] border-l-transparent border-r-transparent border-t-[#00cc88]" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,255,150,0.6))' }} />
        </div>
      </div>

      <style>{`
        @keyframes spinGlow {
          to { transform: rotate(360deg); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes ledBlink {
          0% { opacity: 0.35; }
          100% { opacity: 1; }
        }
        .animate-led-blink {
          animation: ledBlink 1.2s ease-in-out infinite alternate;
        }
      `}</style>

      {(!prizes || prizes.length === 0) && (
        <p className="text-red-400 text-sm mt-4">Please add at least one active prize to spin the wheel</p>
      )}
    </div>
  );
});

SpinningWheel.displayName = 'SpinningWheel';
export default SpinningWheel;
